import {
  getAllDescendants,
  getOriginalPermissionValue,
} from "./permissionsHelpers";

/**
 * Calculate ancestor permission value based on all descendants
 * Now properly handles tracked changes for accurate calculations
 */
export function calculateAncestorPermissionValue(
  rawApiData,
  ancestorId,
  permissionType,
  changedItemId,
  newValue,
  trackedChanges = new Map()
) {
  const descendants = getAllDescendants(rawApiData, ancestorId);

  if (descendants.length === 0) {
    // No descendants, use current permission value
    return getOriginalPermissionValue(rawApiData, ancestorId, permissionType);
  }

  // Check if any descendant has this permission as true
  const hasAnyTrueDescendant = descendants.some((descendant) => {
    // First check if there's a tracked change for this descendant
    // Need to check all possible view keys since we might be calculating across tabs
    const possibleViewKeys = [
      "forms_general",
      "forms_content_general",
      "forms_content_tabs",
    ];

    for (const viewKey of possibleViewKeys) {
      const changeKey = `${viewKey}_${descendant.itemid}_${permissionType}`;
      const trackedChange = trackedChanges.get(changeKey);

      if (trackedChange) {
        return trackedChange.currentValue === true;
      }
    }

    // If this is the item being changed, use the new value
    if (descendant.itemid === changedItemId) {
      return newValue;
    }

    // Otherwise use current permission value from raw data
    return getOriginalPermissionValue(
      rawApiData,
      descendant.itemid,
      permissionType
    );
  });

  return hasAnyTrueDescendant;
}

/**
 * Get dependent permissions for a given permission type
 */
export function getDependentPermissions(
  parentPermissionType,
  subTab,
  contentType = null
) {
  switch (subTab) {
    case "forms":
      if (parentPermissionType === "search_view") {
        return ["create", "edit", "delete", "print"];
      }
      break;

    case "forms_content":
      if (parentPermissionType === "view") {
        // For tabs content type, include print as dependent on view
        if (contentType === "tabs") {
          return ["edit", "print"];
        }
        return ["edit"];
      }
      break;

    case "tools":
      if (parentPermissionType === "view_open") {
        return ["edit"];
      }
      break;

    default:
      return [];
  }

  return [];
}

/**
 * Get the actual permission value from the modified state
 */
export function getActualPermissionValue(
  permissionsState,
  itemId,
  permissionType
) {
  // Search in flat structure (miscellaneous)
  if (permissionsState.isFlat && permissionsState.items) {
    const item = permissionsState.items.find((item) => item.id === itemId);
    return item?.permissions?.[permissionType] || false;
  }

  // Search in hierarchical structure
  if (permissionsState.categories) {
    for (const category of permissionsState.categories) {
      // Check category level
      if (category.id === itemId) {
        return category.permissions?.[permissionType] || false;
      }

      // Search in category items
      for (const item of category.items || []) {
        if (item.id === itemId) {
          return item.permissions?.[permissionType] || false;
        }

        // Handle parent groups (4-level structure)
        if (item.isParentGroup && item.items) {
          if (item.id === itemId) {
            return item.permissions?.[permissionType] || false;
          }

          // Search in forms under parent group
          for (const form of item.items) {
            if (form.id === itemId) {
              return form.permissions?.[permissionType] || false;
            }

            // Search in fields/tabs under forms
            if (form.items) {
              for (const field of form.items) {
                if (field.id === itemId) {
                  return field.permissions?.[permissionType] || false;
                }
              }
            }
          }
        }
        // Handle regular nested items (3-level structure)
        else if (item.items) {
          for (const nestedItem of item.items) {
            if (nestedItem.id === itemId) {
              return nestedItem.permissions?.[permissionType] || false;
            }
          }
        }
      }
    }
  }

  return false;
}
